import type { ButtonHTMLAttributes, ReactNode } from "react";
import { motion, type HTMLMotionProps } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";
import { spring } from "./motion";
import { Spinner } from "./Spinner";

const buttonStyles = cva(
  [
    "relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-semibold tracking-[-0.01em]",
    "transition-[background-color,color,box-shadow,opacity] duration-150 ease-out",
    "disabled:pointer-events-none disabled:opacity-45 aria-busy:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-primary text-on-primary shadow-brand hover:bg-primary-hover",
        accent: "bg-accent text-on-accent shadow-accent hover:bg-accent-hover",
        secondary: "bg-surface text-ink shadow-soft ring-1 ring-line-strong ring-inset hover:bg-sunk",
        ghost: "text-ink hover:bg-ink/[0.05]",
        tonal: "bg-primary-soft text-primary-ink hover:bg-primary-tint",
        danger: "bg-bad text-white shadow-soft hover:bg-bad-ink",
      },
      size: {
        sm: "h-8 px-3 text-[13px] [&_svg]:size-4 before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
        md: "h-10 px-4 text-sm [&_svg]:size-[18px]",
        lg: "h-12 px-6 text-[15px] [&_svg]:size-5",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonStyles> & {
    loading?: boolean;
    icon?: ReactNode;
    ref?: React.Ref<HTMLButtonElement>;
    /** Motion animation controls or target, for one-off feedback such as a shake. */
    animate?: HTMLMotionProps<"button">["animate"];
  };

const tap = { whileTap: { scale: 0.97 }, transition: spring };

export function Button({
  variant,
  size,
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <motion.button
      type={type}
      className={cn(buttonStyles({ variant, size }), className)}
      disabled={disabled}
      aria-busy={loading || undefined}
      {...tap}
      {...(rest as HTMLMotionProps<"button">)}
    >
      <span className={cn("inline-flex items-center gap-2", loading && "invisible")}>
        {icon ? <span aria-hidden className="inline-flex">{icon}</span> : null}
        {children}
      </span>
      {loading ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner label="Loading" className="size-[18px]" />
        </span>
      ) : null}
    </motion.button>
  );
}

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  variant?: "ghost" | "secondary" | "primary" | "accent" | "tonal";
  size?: "sm" | "md";
  ref?: React.Ref<HTMLButtonElement>;
};

export function IconButton({ label, variant = "ghost", size = "md", className, children, type = "button", ...rest }: IconButtonProps) {
  return (
    <motion.button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        buttonStyles({ variant, size: size === "sm" ? "sm" : "md" }),
        size === "sm" ? "size-8 px-0" : "size-10 px-0",
        "before:absolute before:-inset-1 before:content-[''] md:before:inset-0",
        className,
      )}
      whileTap={{ scale: 0.92 }}
      transition={spring}
      {...(rest as HTMLMotionProps<"button">)}
    >
      {children}
    </motion.button>
  );
}
